<script setup lang="ts">
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { X } from 'lucide-vue-next';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/Select';

import { ScrollArea } from '@/components/ui/ScrollArea';
import { Label } from '@/components/ui/Label';

import { cn } from '@/lib/utils';
import { prepareMessage } from '@/lib/helpers';

import {
  STATUS_DEFAULT,
  STATUS_ERROR,
  STATUS_SUCCESS
} from '@/constants/status';

import type { CustomSelectProps } from '@/types/components';

const { t } = useTranslation();

const props = withDefaults(defineProps<CustomSelectProps>(), {
  id: '',
  class: null,
  modelValue: '',
  contentClass: '',
  defaultValue: '',
  hasSmallText: false,
  isCompact: false,
  isDisabled: false,
  isClearable: false,
  messages: () => ({
    errorMessage: '',
    successMessage: ''
  }),
  placeholder: '',
  optionIconClass: '',
  options: () => [],
  validationStatus: STATUS_DEFAULT,
  testId: 'select',
  triggerIcon: null
});

const emits = defineEmits<{
  (e: 'update:modelValue', payload: string): void;
}>();

function clearSelect() {
  emits('update:modelValue', '');
}

const modelValue = useVModel(props, 'modelValue', emits, {
  passive: true
});

const selectModel = ref(modelValue);

const inputMessage = computed(() =>
  prepareMessage(props.validationStatus, props.messages)
);

/*
 * INFO: Scroll inside the select portal will be triggered when content is overflowing in the SelectContent
 * Set max height to SelectContent in order to trigger the scroll
 * */
</script>

<template>
  <div
    :class="cn('h-min w-full', labelText && 'flex flex-col gap-2.5')"
    :data-testid="testId"
  >
    <Label v-if="labelText" :for="id" class="text-black" :data-testid="`${props.testId}-label`">
      {{ $t(labelText) }}
    </Label>
    <div class="gap-0 w-full">
      <Select :id="id" v-model="selectModel as string" :disabled="isDisabled" :test-id="`${props.testId}-select`">
        <SelectTrigger
          :class="
            cn(
              'w-full focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-100 outline-blue-500 focus:ring-offset-0',
              isCompact && 'px-1',
              hasSmallText && 'text-sm',
              props.class
            )
          "
          :test-id="`${props.testId}-trigger`"
          :disabled="isDisabled"
          :is-compact="isCompact"
          :validation-status="validationStatus"
        >
          <div
            :class="
              cn(
                'flex items-center h-full w-full overflow-hidden shrink-0',
                isCompact && 'justify-center'
              )
            "
          >
            <slot name="trigger-icon"></slot>
            <SelectValue
              v-if="!isCompact"
              :test-id="`${props.testId}-value`"
              :placeholder="placeholder || t('manageAccounts.select_an_option')"
              class="truncate"
            />
          </div>

          <template v-if="selectModel && !isDisabled && isClearable">
            <X
              class="w-4 h-4 absolute right-8 top-1/2 -translate-y-1/2 opacity-40 transition duration-300 flex-shrink-0 flex-grow-0 flex hover:opacity-80 rtl:pr-0 rtl:pl-3.5"
              :data-testid="`${props.testId}-clear-select-icon`"
              @pointerdown.stop.prevent
              @click="clearSelect"
            />
          </template>
        </SelectTrigger>
        <SelectContent :test-id="`${props.testId}-content`">
          <ScrollArea :class="contentClass">
            <SelectGroup>
              <SelectItem
                v-for="option in options"
                :key="option?.value"
                :test-id="option?.label"
                :disabled="option?.disabled"
                :value="option?.value as string"
                :class="hasSmallText ? 'text-sm' : null"
                :disabled-content="disabledContent"
              >
                <template #select-item-icon>
                  <div
                    v-if="option?.icon"
                    data-testid="select-option-icon"
                    :class="
                      cn(
                        'w-4 h-4 flex justify-center items-center shrink-0 mr-2 rounded overflow-hidden',
                        props.optionIconClass
                      )
                    "
                  >
                    <img
                      :src="option?.icon"
                      class="w-4 h-4 object-contain"
                      alt=""
                    />
                  </div>
                </template>
                <template #select-item-text>
                  <span
                    :data-testid="
                      props.testId
                        ? `${props.testId}-option-${option.value}`
                        : undefined
                    "
                  >
                    {{ option.label }}
                  </span>
                </template>
              </SelectItem>
            </SelectGroup>
          </ScrollArea>
        </SelectContent>
      </Select>
      <p
        v-if="validationStatus !== STATUS_DEFAULT"
        :data-testid="`${props.testId}-validation-message`"
        :class="
          cn(
            'text-sm mt-1',
            validationStatus === STATUS_ERROR && 'text-red-600',
            validationStatus === STATUS_SUCCESS && 'text-emerald-600'
          )
        "
      >
        {{ inputMessage }}
      </p>
    </div>
  </div>
</template>
